import {Barcode, Platform} from "@variocube/app-api";
import {defined, EventSupportingApp, occupyAndConfirm} from "@variocube/app-common";
import {AppContext, AppErrorBoundary} from "@variocube/cube-ui";
import * as React from "react";
import {render} from "react-dom";
import dpdIcon from "./dpd-icon.svg";
import glsIcon from "./gls-icon.svg";
import upsIcon from "./ups-icon.svg";
import dhlIcon from "./dhl-icon.svg";
import physicalIcon from "./physical-icon.png";
import wienboxIcon from "./Wienbox_Logo_klein HG_Blau.png";
import greenPickupIcon from "./green-pickup-icon.svg";
import kepoevIcon from "./kepoev-icon.png";
import {BackendApiCredentials, Carriers, CubeCarrier, CubeCarrierState, DeliveryOccupancyContent} from "./types";
import {App} from "./app";
import storeboxLogo from "./storebox-logo.svg";
import {CarrierProvider} from "./CarrierProvider";
import {parseBarcode} from "./barcode";
import {DialogMessageProvider} from './DialogMessageProvider';
import {MessageDialog} from './MessageDialog';
import {LocalizationProvider, SUPPORTED_LANGUAGES} from "../i18n";

export default class extends EventSupportingApp {

    private carriers?: Carriers;

    constructor(p: Platform) {
        super(p, dpdIcon, "#fff", SUPPORTED_LANGUAGES);
    }

    render(root: Element) {
        const appContext = {
            platform: this.platform,
            appEventSupport: this.eventSupport
        };
        render((
            <AppContext.Provider value={appContext}>
                <AppErrorBoundary appId={this.platform.appId} cubeId={this.platform.hostname}>
                    <CarrierProvider>
                        <DialogMessageProvider>
                            <LocalizationProvider app={this}>
                                <App onCarriers={this.handleCarriers}/>
                                <MessageDialog />
                            </LocalizationProvider>
                        </DialogMessageProvider>
                    </CarrierProvider>
                </AppErrorBoundary>
            </AppContext.Provider>
        ), root);
    }

    private handleCarriers = (carriers: Carriers) => {
        this.carriers = carriers;
        this.updateLauncherItems();
    }

    computeLauncherItems() {
        return Object.keys(this.carriers ?? {})
            .map(carrier => this.getLauncherItem(carrier))
            .filter(defined);
    }

    getLauncherItem(carrierId: string) {
        switch (carrierId) {
            case "dpd": return {
                id: "dpd",
                title: "DPD",
                description: this.description,
                icon: dpdIcon,
                color: "#fff",
            }
            case "gls": return {
                id: "gls",
                title: "GLS",
                description: this.description,
                icon: glsIcon,
                color: "#061ab1",
            }
            case "ups": return {
                id: "ups",
                title: "UPS",
                description: this.description,
                icon: upsIcon,
                color: "#fab80a",
            }
            case "dhl": return {
                id: "dhl",
                title: "DHL",
                description: this.description,
                icon: dhlIcon,
                color: "#ffcc00",
            }
            case "physical": return {
                id: "physical",
                title: "PhysICAL",
                description: this.description,
                icon: physicalIcon,
                color: "#efefef",
            }
            case "wienbox": return {
                id: "wienbox",
                title: "WienBox",
                description: this.description,
                icon: wienboxIcon,
                color: "#09255B",
            }
            case "greenpickup": return {
                id: "greenpickup",
                title: "GreenPickup",
                description: this.description,
                icon: greenPickupIcon,
                color: "#FFFFFF",
            }
            case "kepoev": return {
                id: "kepoev",
                title: "Öffi Packerl",
                description: this.description,
                icon: kepoevIcon,
                color: "#FFFFFF",
            }
        }

    }


    canReadBarcode({code}: Barcode) {
        return defined(parseBarcode(atob(code)));
    }

    async loadMockData() {

        // create a delivery that can be stored with a barcode like %000111006210123456781332040
        // const delivery = await occupyAndConfirm(this.platform, {
        //     accessCode: "1",
        //     type: "M",
        //     content: {
        //         id: "mock-occupancy-1",
        //         carrierId: "dpd",
        //         deliveryId: "06210123456781",
        //         carrierDeliveryCode: "1",
        //         recipientPickupCode: "2",
        //         carrierRetrievalCode: "3",
        //     } as DeliveryOccupancyContent
        // });
        // await this.platform.changeAccessKeys(delivery.uuid, ["06210123456781"]);
        //console.info("created occupancy for delivery", delivery);

        const delivery2 = await occupyAndConfirm(this.platform, {
            accessCode: "22",
            type: "M",
            content: {
                id: "mock-occupancy-1",
                carrierId: "ups",
                deliveryId: "1ZX5R6436803666119",
                carrierDeliveryCode: "22",
                recipientPickupCode: "2",
                carrierRetrievalCode: "3",
            } as DeliveryOccupancyContent,
        });
        await this.platform.changeAccessKeys(delivery2.uuid, ["1ZX5R6436803666119"]);
        console.info("created occupancy for delivery", delivery2);

        const delivery3 = await occupyAndConfirm(this.platform, {
            accessCode: "33",
            type: "M",
            content: {
                id: "mock-occupancy-1",
                carrierId: "dhl",
                deliveryId: "JD0000295694048310306528",
                carrierDeliveryCode: "33",
                recipientPickupCode: "4",
                carrierRetrievalCode: "5",
            } as DeliveryOccupancyContent,
        });
        await this.platform.changeAccessKeys(delivery3.uuid, ["JD0000295694048310306528"]);

        const delivery4 = await occupyAndConfirm(this.platform, {
            accessCode: "44",
            type: "L",
            content: {
                id: "mock-occupancy-4",
                carrierId: "physical",
                deliveryId: "4e743bf7-fb4b-448a-9d96-c9fe095f75b6",
                carrierDeliveryCode: "33",
                recipientPickupCode: "44",
                carrierRetrievalCode: "55",
            } as DeliveryOccupancyContent,
        });
        await this.platform.changeAccessKeys(delivery4.uuid, ["JD0000295694048310306528"]);

        // const pickup = await occupyAndConfirm(this.platform, {
        //     accessCode: "2",
        //     type: "M",
        //     content: {
        //         id: "mock-occupancy-2",
        //         carrierId: "",
        //         deliveryId: "06210123456782",
        //         carrierDeliveryCode: "1",
        //         recipientPickupCode: "2",
        //         carrierRetrievalCode: "3",
        //     } as DeliveryOccupancyContent
        // });
        // console.info("created occupancy for pickup", pickup);

        // const retrieval1 = await occupyAndConfirm(this.platform, {
        //     accessCode: "3",
        //     type: "M",
        //     content: {
        //         id: "mock-occupancy-3",
        //         carrierId: "dpd",
        //         deliveryId: "06210123456783",
        //         carrierDeliveryCode: "1",
        //         recipientPickupCode: "2",
        //         carrierRetrievalCode: "3",
        //     } as DeliveryOccupancyContent
        // });
        // console.info("created occupancy for retrieval", retrieval1);

        // const retrieval2 = await occupyAndConfirm(this.platform, {
        //     accessCode: "4",
        //     type: "M",
        //     content: {
        //         id: "mock-occupancy-4",
        //         carrierId: "dpd",
        //         deliveryId: "06210123456784",
        //         carrierDeliveryCode: "1",
        //         recipientPickupCode: "2",
        //         carrierRetrievalCode: "4",
        //     } as DeliveryOccupancyContent
        // });
        // console.info("created occupancy for retrieval", retrieval2);


        const pickup2 = await occupyAndConfirm(this.platform, {
            accessCode: "123456",
            type: "L",
            content: {
                id: "mock-occupancy-5",
                carrierId: "ups",
                deliveryId: "1ZX5R643QH21772494",
                carrierDeliveryCode: "1",
                recipientPickupCode: "123456",
                carrierRetrievalCode: "3",
            } as DeliveryOccupancyContent,
        });
        console.info("created occupancy for pickup", pickup2);

        const pickup3 = await occupyAndConfirm(this.platform, {
            accessCode: "555333",
            type: "S",
            content: {
                id: "mock-occupancy-5",
                carrierId: "ups",
                deliveryId: "1ZX5R6436901804800",
                carrierDeliveryCode: "1",
                recipientPickupCode: "555333",
                carrierRetrievalCode: "3",
                packageReleaseCodeRequired: true,
            } as DeliveryOccupancyContent,
        });
        console.info("created occupancy for pickup", pickup3);

        const pickup4 = await occupyAndConfirm(this.platform, {
            accessCode: "66",
            type: "S",
            content: {
                id: "mock-occupancy-6",
                carrierId: "dhl",
                deliveryId: "JD0453909816010101010101",
                carrierDeliveryCode: "1",
                recipientPickupCode: "66",
                carrierRetrievalCode: "3",
            } as DeliveryOccupancyContent,
        });
        console.info("created occupancy for pickup", pickup4);

        const retrieval3 = await occupyAndConfirm(this.platform, {
            accessCode: "6",
            type: "M",
            content: {
                id: "mock-occupancy-6",
                carrierId: "ups",
                deliveryId: "1ZX5R643QH21772495",
                carrierDeliveryCode: "1",
                recipientPickupCode: "3",
                carrierRetrievalCode: "6",
            } as DeliveryOccupancyContent,
        });
        console.info("created occupancy for retrieval", retrieval3);

        const retrieval4 = await occupyAndConfirm(this.platform, {
            accessCode: "55",
            type: "L",
            content: {
                id: "mock-occupancy-6",
                carrierId: "ups",
                deliveryId: "1ZX5R643QH21772496",
                carrierDeliveryCode: "1",
                recipientPickupCode: "3",
                carrierRetrievalCode: "55",
            } as DeliveryOccupancyContent,
        });
        console.info("created occupancy for retrieval", retrieval4);

        const retrieval5 = await occupyAndConfirm(this.platform, {
            accessCode: "565656",
            type: "S",
            content: {
                id: "mock-occupancy-6",
                carrierId: "ups",
                deliveryId: "1ZX5R6436830966520",
                carrierDeliveryCode: "1",
                recipientPickupCode: "3",
                carrierRetrievalCode: "565656",
            } as DeliveryOccupancyContent,
        });
        console.info("created occupancy for retrieval", retrieval5);

        return {
            tenantLogoUrl: storeboxLogo,
            tenantPhone: "+43 720 676 300",
            backendApiCredentials: {
                baseUrl: "https://t-kep.yourstorebox.com",
                token: "bi7lwKf2tiWCEclQYQBy4dP7Zy3BqqTMHEp1ZOv9fQZuULbzpetaHbfgl4a8PIGl"
            } as BackendApiCredentials,
            carriers: {
                dpd: {
                    accessCode: "999",
                    pin: "123",
                    state: CubeCarrierState.Enabled
                } as CubeCarrier,
                // gls: {
                //     accessCode: "888",
                //     pin: "123"
                // } as CubeCarrier,
                ups: {
                    accessCode: "777",
                    signatureOutputFormat: "JsonPointList",
                    autoCarrierRetrieval: true,
                    messageDialogConfiguration: {
                        hideCloseButton: true,
                        autoCloseTimer: 10000,
                    },
                    lastMile: {
                        recipientNameRequired: false,
                        reopenCountdown: 1,
                        carrierInterfaceShowsDeliveryOption: true,
                    },
                    firstMile: {
                        availableFields: [
                        ],
                        requiredFields: [
                        ],
                        allContactFieldsRequired: false,
                        verifyContactData: true,
                        reopenCountdown: 1,
                        offerReceipt: true,
                    },
                    state: CubeCarrierState.Enabled
                } as CubeCarrier,
                dhl: {
                    accessCode: "666",
                    lastMile: {
                        recipientNameRequired: true,
                        carrierInterfaceShowsDeliveryOption: false,
                    },
                    state: CubeCarrierState.LastMileOnly
                } as CubeCarrier,
                physical: {
                    accessCode: "999",
                    lastMile: {
                        recipientNameRequired: false,
                        carrierInterfaceShowsDeliveryOption: false,
                    },
                    state: CubeCarrierState.Enabled
                } as CubeCarrier,
                wienbox: {
                    accessCode: "991",
                    lastMile: {
                        recipientNameRequired: true,
                        carrierInterfaceShowsDeliveryOption: false,
                    },
                    state: CubeCarrierState.Enabled
                } as CubeCarrier,
                greenpickup: {
                    accessCode: "992",
                    lastMile: {
                        recipientNameRequired: true,
                        carrierInterfaceShowsDeliveryOption: false,
                    },
                    state: CubeCarrierState.Enabled
                } as CubeCarrier
            } as Carriers,
            expiredParcelIds: ['666', '999']
        };
    }
}


